import { Alert, Box, Typography } from "@mui/material";
import { useTranslate } from "react-admin";

import {
  type LogisticsWorkspaceOneDeviceFragment,
  type LogisticsWorkspaceOneMatchFragment,
  LogisticsWorkspaceOneMatchState,
} from "../../generated/graphql";
import { CRM_COLORS, formatCrmDateTime } from "../../lib/crm";
import { CrmFieldList } from "../crm/CrmSection";

export const inventoryWorkspaceOneUrl = (
  type: string | null | undefined,
  workspaceOne: LogisticsWorkspaceOneMatchFragment | null | undefined
): string | null =>
  type === "Tablet" && workspaceOne?.state === LogisticsWorkspaceOneMatchState.Linked
    ? (workspaceOne.device?.url ?? null)
    : null;

const Facts = ({ fields, compact }: { fields: [string, string][]; compact: boolean }) =>
  compact ? (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        gap: "2px 8px",
        m: 0,
      }}
    >
      {fields.map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography component="dt" sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
            {label}
          </Typography>
          <Box
            component="dd"
            aria-label={label}
            sx={{ m: 0, fontSize: "12px", color: CRM_COLORS.text, overflowWrap: "anywhere" }}
          >
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  ) : (
    <CrmFieldList items={fields.map(([label, value]) => ({ key: label, label, value, valueAriaLabel: label }))} />
  );

export const WorkspaceOneDeviceFacts = ({
  device,
  syncedAt,
  compact = false,
}: {
  device: LogisticsWorkspaceOneDeviceFragment | null;
  syncedAt: string | null;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const unknown = translate("logistics.workspaceOne.unknown");
  const enrollment = device?.enrollmentStatus;
  const enrollmentKey = enrollment?.trim().toLowerCase();
  const fields: [string, string][] = device
    ? [
        [
          translate("logistics.workspaceOne.enrollment"),
          enrollmentKey === "enrolled" || enrollmentKey === "disenrolled"
            ? translate(`logistics.workspaceOne.${enrollmentKey}`)
            : enrollment || unknown,
        ],
        [
          translate("logistics.workspaceOne.lastSeenAt"),
          device.lastSeenAt ? formatCrmDateTime(device.lastSeenAt) : unknown,
        ],
        [translate("logistics.workspaceOne.compliance"), device.complianceStatus || unknown],
        [translate("logistics.workspaceOne.model"), device.model || unknown],
        [translate("logistics.workspaceOne.operatingSystem"), device.operatingSystem || unknown],
      ]
    : [];
  fields.push([translate("logistics.workspaceOne.syncedAt"), syncedAt ? formatCrmDateTime(syncedAt) : unknown]);
  const appName = (bundleId: string): string => {
    if (bundleId === "com.media4care.b2clic") return translate("logistics.workspaceOne.apps.senior");
    if (bundleId === "com.media4care.b2blic") return translate("logistics.workspaceOne.apps.caregiver");
    return translate("logistics.workspaceOne.apps.unknown");
  };

  return (
    <>
      <Facts fields={fields} compact={compact} />
      {device?.apps.map(app => (
        <Box component="section" aria-label={appName(app.bundleId)} key={app.appUuid}>
          <Typography
            component="h4"
            sx={{
              m: "0 0 4px",
              fontSize: "13px",
              fontWeight: 700,
              color: CRM_COLORS.text,
              ...(compact ? {} : { px: "16px" }),
            }}
          >
            {appName(app.bundleId)}
          </Typography>
          <Facts
            compact={compact}
            fields={[
              [translate("logistics.workspaceOne.installedVersion"), app.installedVersion || unknown],
              [translate("logistics.workspaceOne.assignedVersion"), app.assignedVersion || unknown],
              [translate("logistics.workspaceOne.installedStatus"), app.installedStatus || unknown],
              [translate("logistics.workspaceOne.assignmentStatus"), app.assignmentStatus || unknown],
              [
                translate("logistics.workspaceOne.lastReportedAt"),
                app.lastReportedAt ? formatCrmDateTime(app.lastReportedAt) : unknown,
              ],
            ]}
          />
        </Box>
      ))}
      {device?.apps.length === 0 && (
        <Typography
          sx={{ fontSize: compact ? "12px" : "13px", color: CRM_COLORS.textWeak, ...(compact ? {} : { px: "16px" }) }}
        >
          {translate("logistics.workspaceOne.noSupportedApps")}
        </Typography>
      )}
    </>
  );
};

export const InventoryWorkspaceOne = ({
  workspaceOne,
  compact = false,
}: {
  workspaceOne?: LogisticsWorkspaceOneMatchFragment | null;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const state = workspaceOne?.state ?? LogisticsWorkspaceOneMatchState.Unavailable;
  const device = state === LogisticsWorkspaceOneMatchState.Linked ? (workspaceOne?.device ?? null) : null;
  return (
    <Box sx={{ display: "grid", gap: compact ? "6px" : "12px", ...(compact ? {} : { pb: "16px" }) }}>
      {state !== LogisticsWorkspaceOneMatchState.Linked &&
        (compact ? (
          <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>
            {translate(`logistics.workspaceOne.states.${state}`)}
          </Typography>
        ) : (
          <Alert severity="info" sx={{ mx: "16px" }}>
            {translate(`logistics.workspaceOne.states.${state}`)}
          </Alert>
        ))}
      {workspaceOne?.stale && (
        <Alert severity="warning" sx={compact ? { p: "0 8px", fontSize: "12px" } : { mx: "16px" }}>
          {translate("logistics.workspaceOne.stale")}
        </Alert>
      )}
      <WorkspaceOneDeviceFacts device={device} syncedAt={workspaceOne?.syncedAt ?? null} compact={compact} />
    </Box>
  );
};
